/**
 * Custom Refine data provider that uses direct coreBOS API
 *
 * This provider handles:
 * - Standard CRUD operations via direct API calls (bypasses MCP)
 * - Deterministic operations for fast execution
 * - AI-assisted operations remain in AssistantPanel
 */

import { DataProvider } from "@refinedev/core";
import axios, { AxiosInstance } from "axios";

// Module to MCP resource mapping
const MODULE_MAP: Record<string, string> = {
  accounts: "Accounts",
  contacts: "Contacts",
  leads: "Leads",
  opportunities: "Potentials",
  tickets: "HelpDesk",
  events: "cbCalendar",
  activities: "cbCalendar",
  tasks: "cbCalendar",
  documents: "Documents",
  "sales-orders": "cbSOMaster",
  "sales-order-details": "cbSODetails",
  products: "Products",
  services: "Services",
  offers: "cbOffers",
  "offer-details": "cbOfferDetail",
  invoices: "InvoiceMaster",
  "invoice-details": "InvoiceDetails",
  projects: "Project",
  "project-tasks": "ProjectTask",
};

const getModuleName = (resource: string): string => {
  return MODULE_MAP[resource] || resource;
};

export const createMCPDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axios.create()
): DataProvider => {
  // Direct API calls - bypass MCP for standard CRUD operations
  const directQuery = async (query: string) => {
    const response = await httpClient.post(`${apiUrl}/direct/query`, { query });

    if (!response.data.success) {
      throw new Error(response.data.error || "Query failed");
    }

    return response.data.result;
  };

  const directRetrieve = async (id: string) => {
    const response = await httpClient.post(`${apiUrl}/direct/retrieve`, { id });

    if (!response.data.success) {
      throw new Error(response.data.error || "Retrieve failed");
    }

    return response.data.result;
  };

  const directCreate = async (
    module: string,
    data: Record<string, unknown>
  ) => {
    const response = await httpClient.post(`${apiUrl}/direct/create`, {
      module,
      data,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || "Create failed");
    }

    return response.data.result;
  };

  const directUpdate = async (id: string, data: Record<string, unknown>) => {
    const response = await httpClient.post(`${apiUrl}/direct/update`, {
      id,
      data,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || "Update failed");
    }

    return response.data.result;
  };

  const directDelete = async (id: string) => {
    const response = await httpClient.post(`${apiUrl}/direct/delete`, { id });

    if (!response.data.success) {
      throw new Error(response.data.error || "Delete failed");
    }

    return response.data.result;
  };

  return {
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      const module = getModuleName(resource);
      const { current = 1, pageSize = 20 } = pagination ?? {};

      // Check for raw VQL conditions (from coreBOS views)
      let rawConditions: string | undefined;
      const filterList: { field: string; operator: string; value: unknown }[] =
        [];

      filters?.forEach((filter) => {
        if ("field" in filter && filter.value !== undefined) {
          const { field, operator, value } = filter;

          // Special case: raw VQL conditions from views
          if (
            field === "conditions" &&
            operator === "eq" &&
            typeof value === "string"
          ) {
            rawConditions = value;
            return;
          }

          // Skip internal search field - handled separately by FilterBar
          if (field === "_search") {
            return;
          }

          // Map Refine operators to VQL operators
          let vqlOperator = "=";
          switch (operator) {
            case "eq":
              vqlOperator = "=";
              break;
            case "ne":
              vqlOperator = "!=";
              break;
            case "contains":
              vqlOperator = "like";
              break;
            case "startswith":
              vqlOperator = "starts";
              break;
            case "gt":
              vqlOperator = ">";
              break;
            case "lt":
              vqlOperator = "<";
              break;
            case "gte":
              vqlOperator = ">=";
              break;
            case "lte":
              vqlOperator = "<=";
              break;
            default:
              vqlOperator = "=";
          }
          filterList.push({ field, operator: vqlOperator, value });
        }
      });

      // Build sort object
      const sort = sorters?.[0]
        ? { field: sorters[0].field, order: sorters[0].order }
        : undefined;

      // Use new list endpoint with count
      const requestBody = {
        fields: meta?.fields,
        filters: filterList.length > 0 ? filterList : undefined,
        conditions: rawConditions, // Raw VQL WHERE clause
        sort,
        page: current,
        pageSize,
      };

      const response = await httpClient.post(
        `${apiUrl}/modules/${module}/list`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "List failed");
      }

      return {
        data: response.data.data || [],
        total: response.data.total || 0,
      };
    },

    getOne: async ({ id }) => {
      const data = await directRetrieve(String(id));
      return { data };
    },

    create: async ({ resource, variables }) => {
      const module = getModuleName(resource as string);
      const data = await directCreate(
        module,
        variables as Record<string, unknown>
      );
      return { data };
    },

    update: async ({ id, variables }) => {
      const data = await directUpdate(
        String(id),
        variables as Record<string, unknown>
      );
      return { data };
    },

    deleteOne: async ({ id }) => {
      const data = await directDelete(String(id));
      return { data };
    },

    getMany: async ({ ids }) => {
      // Filter out invalid IDs (empty, null, undefined, or not in WSID format)
      const validIds = ids.filter((id) => {
        const strId = String(id);
        return (
          strId &&
          strId !== "undefined" &&
          strId !== "null" &&
          strId.includes("x")
        );
      });

      if (validIds.length === 0) {
        return { data: [] };
      }

      // Fetch each record individually (could be optimized with MassRetrieve)
      const promises = validIds.map((id) => directRetrieve(String(id)));
      const results = await Promise.all(promises);
      return { data: results };
    },

    createMany: async ({ variables, meta }) => {
      const module = getModuleName(meta?.resource || "");
      const promises = variables.map((vars) =>
        directCreate(module, vars as Record<string, unknown>)
      );
      const results = await Promise.all(promises);
      return { data: results };
    },

    deleteMany: async ({ ids }) => {
      const promises = ids.map((id) => directDelete(String(id)));
      const results = await Promise.all(promises);
      return { data: results };
    },

    updateMany: async ({ ids, variables }) => {
      const promises = ids.map((id) =>
        directUpdate(String(id), variables as Record<string, unknown>)
      );
      const results = await Promise.all(promises);
      return { data: results };
    },

    // Custom method for special operations
    custom: async ({ url, method, payload, headers }) => {
      // Handle direct VQL queries
      if (url === "/direct/query") {
        const data = await directQuery((payload as { query: string }).query);
        return { data };
      }

      // Fall back to regular HTTP call
      const response = await httpClient.request({
        url: `${apiUrl}${url}`,
        method,
        data: payload,
        headers,
      });

      return { data: response.data };
    },

    getApiUrl: () => apiUrl,
  };
};
